// The shortlist's judgement of a move: scores read and written through the
// review API's `Score`, the engine's lines in scoresheet terms, and whether
// the played move was the engine's choice, a small loss or a blunder.
// Pure: no React, no DOM.
import { uciToInput, type Chess } from '@chess/api-chess';
import type { EngineLine, EngineScore } from '@chess/api-engine';
import { Score, isBlunderLoss, type MoveGrade } from '@chess/api-review';
import type { Fen, MistakeRules, Uci } from '@chess/api-model';

export type Verdict = 'best' | 'blunder' | 'other';

/** How many moves of each line to show after the first. */
export const CONTINUATION = 3;

// Scores are for the side to move and go through the review API's `Score`,
// so this panel writes and compares them exactly as every other panel does.
export const centipawns = (score: EngineScore): number => Score.of(score).centipawns();
export const formatScore = (score: EngineScore): string => Score.of(score).text();
/** The engine scores for the side to move; the move just played was by the other side. */
export const negate = (score: EngineScore): EngineScore => Score.of(score).flipped().toJSON();

/** The first moves of a line in scoresheet terms, played out from the position. */
export function describe(chess: Chess, fen: Fen, pv: readonly Uci[], continuation = CONTINUATION): { san: string; continuation: string; valid: boolean } {
  let current = fen;
  const sans: string[] = [];
  for (const uci of pv.slice(0, 1 + continuation)) {
    const result = chess.play(current, uciToInput(uci));
    if (!result) break;
    sans.push(result.move.san);
    current = result.fen;
  }
  // A line whose first move does not play here belongs to another position:
  // an update left over from a superseded search. `valid` says so.
  return { san: sans[0] ?? pv[0], continuation: sans.slice(1).join(' '), valid: sans.length > 0 };
}

/**
 * The played move against the deeper search's best: a blunder when the loss
 * crosses the user's blunder line (`isBlunderLoss`, the same rule as the
 * app's grade), so the board's red arrow agrees with the scoresheet.
 */
export function verdictFor(best: EngineLine | undefined, played: EngineScore | undefined, playedUci: Uci, rules: MistakeRules): Verdict | undefined {
  if (!best || !played) return undefined;
  if (best.pv[0] === playedUci) return 'best';
  const loss = centipawns(best.score) - centipawns(played);
  if (loss <= 0) return 'best';
  return isBlunderLoss(loss, rules) ? 'blunder' : 'other';
}

/** The app's grade of a move (as the scoresheet shows it) as the shortlist's verdict. */
export function verdictOfGrade(grade: MoveGrade): Verdict {
  return grade === 'best' ? 'best' : grade === 'blunder' ? 'blunder' : 'other';
}
